import React from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import GatherInfoGoalsIntro from './GatherInfoGoalsIntro';
// import GatherInfoGoalsMain from './GatherInfoGoalsMain';
import GatherInfoGoalsActivities from './GatherInfoGoalsActivities';
import GatherInfoGoalsPriority from './GatherInfoGoalsPriority';
import GatherInfoGoalsPlatforms from './GatherInfoGoalsPlatforms';
import GatherInfoGoalsReview from './GatherInfoGoalsReview';
import { SUBTABS, GOALS_STEPS } from './ConstPaths';
import { isActivityLowTech } from './ConstActivityBtns';
import { findOtherIndex, getOtherEntryFromArray, OTHER_CKBX_VAL, OTHER_DISPLAY_SEP } from './OtherCheckboxUtils';

//Manage the wizard form for the Gather Info / Goals & Needs sub-tab
//Initialize the forms with props and manage those as local state.
//On Continue, update the global Session state with the local state from the forms. 
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

//the steps of the wizard form:
const steps = GOALS_STEPS;

const BASE_URL = SUBTABS.GOALS + "/";
const NEXT_SECTION = SUBTABS.CONTEXTS;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  mainGoal: PropTypes.string,
  activities: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  platforms: PropTypes.arrayOf(PropTypes.string),
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  mainGoal: '',
  activities: [],
  priority: "",
  platforms: []
};

class GatherInfoGoals extends React.Component {
  constructor(props) {
    super(props);
    //manage the incoming values as local state
    this.state = {
      mainGoal: this.props.mainGoal,
      activities: this.props.activities ?? [],
      priority: this.props.priority,
      platforms: this.props.platforms ?? [],
      //this state is for managing interaction between priority and activities:
      originalPriority: this.props.priority,
      autoSetPriorityOne: false,
      //this is for experimenting with database update problem
      originalActivities: this.props.activities,
      //local state for 'other' entries
      otherActivity: getOtherEntryFromArray(this.props.activities),
      otherPlatform: getOtherEntryFromArray(this.props.platforms),
    };

    this.saveActivitiesToSession = this.saveActivitiesToSession.bind(this);
    this.savePriorityToSession = this.savePriorityToSession.bind(this);
    this.savePlatformsToSession = this.savePlatformsToSession.bind(this);
    this.adjustPriorityIfNeeded = this.adjustPriorityIfNeeded.bind(this);
    this.onContinue = this.onContinue.bind(this);
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/goals/intro' would have step of 'intro'
    const stepParam = this.props.match.params.step;
    if(stepParam){
      console.log("GatherInfoGoals.getCurrentStep: got step from URL");
      return(stepParam);
    }
    else{
      console.log("GatherInfoGoals.getCurrentStep: using default step");
      return(steps.INTRO);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      case steps.INTRO:
        nextStep = steps.ACTIVITIES;
        break;
      case steps.ACTIVITIES:
        const chosenActivities = this.state.activities;
        let priority = this.adjustPriorityIfNeeded();

        //process branching and priority activity special cases:
        if (chosenActivities.length === 0){
          nextStep = steps.PLATFORMS;
        }
        else if (chosenActivities.length === 1){
          //skip priority step for sure, and platforms step if activity is low-tech
          if (isActivityLowTech(priority)){
            nextStep = steps.REVIEW;
          }
          else{
            nextStep = steps.PLATFORMS;
          }
        }
        //2 or more activities chosen
        else{ 
          nextStep = steps.PRIORITY;
        }
        break;
      case steps.PRIORITY:
        //don't ask about platforms if priority activity js low-tech
        if (isActivityLowTech(this.state.priority)){
          nextStep = steps.REVIEW;
        }
        else {
          nextStep = steps.PLATFORMS;
        }
        break;
      case steps.PLATFORMS:
        nextStep = steps.REVIEW;
        break;
      case steps.REVIEW:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.INTRO;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  saveActivitiesToSession(){
    this.props.updateSession('activities', this.state.activities);
    //because local Priority may be adjusted based on Activities, save Priority too.
    this.savePriorityToSession();
  }
  savePriorityToSession(){
    this.props.updateSession('priority', this.state.priority);
  }
  savePlatformsToSession(){
    this.props.updateSession('platforms', this.state.platforms);
  }

  //called from the child form when the user changes anything
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;
      //TODO make this special handling a function, and use for GatherInfoContexts, too
      //handle 'activities' differently, since that holds an array rather than a single value
      if(key === 'activities'){
        let newItem=target.value;
        // const newItemIndex = this.state.activities.indexOf(newItem);
        const newItemIndex = this.state.activities.findIndex((activity) => activity.startsWith(newItem));
        let activities = [...this.state.activities];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          activities.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          activities.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherActivity': ""});
        }
        value = activities;
      }
      if(key === 'platforms'){
        const newItem=target.value;
        const newItemIndex = this.state.platforms.findIndex((p) => p.startsWith(newItem));
        let platforms = [...this.state.platforms];
        //if a platform is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          platforms.push(newItem);
        }
        //platform unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          platforms.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherPlatform': ""});
        }
        value = platforms;
      }
      console.log("Updating GatherInfoGoals state with key: " + key + " value: " + value);
      //For Activities, we adjust Priority after setting the local Activity state
      if (key === 'activities')
        this.setState({ [key]: value }, this.adjustPriorityIfNeeded);
      else if (key === 'otherActivity')
        this.setState({ [key]: value }, this.adjustActivities);
      else if (key === 'otherPlatform')
        this.setState({ [key]: value }, this.adjustPlatforms);
      else this.setState({ [key]: value });
    }
  }

  //after setting 'otherActivity', adjust the activities array to include it.
  //this also adjusts the priority in the state if needed
  adjustActivities(){
    let otherActivity = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherActivity;
    let activities = [...this.state.activities];
    const otherItemIndex = findOtherIndex(this.state.activities);
    if( otherItemIndex > -1){
      activities[otherItemIndex] = otherActivity;
      this.setState({ 'activities': activities}, this.adjustPriorityIfNeeded);
    }
  }

  adjustPlatforms(){
    let otherPlatform = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherPlatform;
    let platforms = [...this.state.platforms];
    const otherItemIndex = findOtherIndex(this.state.platforms);
    if( otherItemIndex > -1){
      platforms[otherItemIndex] = otherPlatform;
      this.setState({ 'platforms': platforms});
    }
  }

  //Handles the interaction edge cases between Activities and Priority Activity,
  //to adjust the local PriorityActivity based on what Activities are currently chosen.
  //called when we set the local Activities state, and when we Continue from the Activities form.
  //NOTE: we tried doing this on unmount from Activities, but it was unable to set and save Priority during that unmount.
  adjustPriorityIfNeeded(){
    const pa = this.state.priority;
    const chosenActivities = this.state.activities;
    const originalPriority = this.state.originalPriority;
    const autoSetPriorityOne = this.state.autoSetPriorityOne;

    let newPa;
    // console.log("********************************");
    // console.log("***called adjustPriorityIfNeeded: orig is " + originalPriority + " current is " + pa);

    //1. Keep the original Priority you came in with, as long as it's still selected in chosenActivities 
    if(chosenActivities.includes(originalPriority)){
      newPa = originalPriority;
      this.setState({ 'priority': newPa}); //, () => {console.log("after SetState, Priority updated to: " + this.state.priority);});
    }
    //2. otherwise, if the current Priority isn't in chosenActivities, it's no longer valid, so we want to adjust it
    else if(!chosenActivities.includes(pa)){
        //2a. If only 1 Activity, then that is the Priority
        if (chosenActivities.length === 1){
          console.log("2a. set priority to the only activity");
          newPa = chosenActivities[0];
          this.setState({ 'autoSetPriorityOne': true});
        }
        //2b. If more than 1, we don't know, so empty the Priority
        else{
          console.log("2b. erase priority");
          newPa = "";
        }
      this.setState({ 'priority': newPa}); //, () => {console.log("after SetState, Priority updated to: " + this.state.priority);});
    }
    //3. cover the case where it autoset to the single Activity earlier, but now there are 2+ Activities
    //(I don't think we need to unset the autoSet flag)
    else if (autoSetPriorityOne && chosenActivities.length > 1){
      console.log("3. erase priority after previous autoSet");
      newPa = "";
      this.setState({ 'priority': newPa}); //, () => {console.log("after SetState, Priority updated to: " + this.state.priority);}); 
    }

    //We also return the new priority (or the old one if unchanged)
    //This is because setState might not be complete by the time we need to check the priority, so don't rely on this.state 
    if(newPa !== null){
      return(newPa);
    }
    else return originalPriority;
    //console.log("********************************");
  }
  
  componentDidUpdate(prevProps, prevState){
    console.log("enter componentDidUpdate");
    // console.log(" Activities:");
    // console.log("  current props: "+ this.props.activities);
    // console.log("  prevProps: " + prevProps.activities);
    // console.log("  prevState: " + prevState.activities);
    // console.log("  currentState: " + this.state.activities);
    // console.log("  original state: " + this.state.originalActivities);
    
    // console.log(" Priority:");
    // console.log("  current props: "+ this.props.priority);
    // console.log("  prevProps: " + prevProps.priority);
    // console.log("  prevState: " + prevState.priority);
    // console.log("  currentState: " + this.state.priority);
    // console.log("  original state: " + this.state.originalPriority);

    if (_.isEqual(this.props.activities, prevProps.activities) === false){
      console.log("--incoming props: " + this.props.activities + " differ from prev props: " + prevProps.activities);
      this.setState({'activities': this.props.activities});
    };
    if (this.props.priority !== prevProps.priority){
        console.log("--incoming props: " + this.props.priority + " differ from prev props: " + prevProps.priority);
    //     console.log("--incoming props: " + this.props.priority + " differ from local state: " + this.state.originalPriority);
        this.setState({'priority': this.props.priority});
      }
    if (_.isEqual(this.props.platforms, prevProps.platforms) === false){
      console.log("--incoming props: " + this.props.platforms + " differ from prev props: " + prevProps.platforms);
      this.setState({'platforms': this.props.platforms});
    };
  
  }
  
  render() {
    console.log("rendering GatherInfoGoals, with props: " + JSON.stringify(this.props));
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;

    return(
    <>
    <Switch>
      <Route path={BASE_URL+ steps.INTRO}>
        <GatherInfoGoalsIntro
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
            />
      </Route>
      {/* <Route path={BASE_URL + steps.MAIN}>
        <GatherInfoGoalsMain
              key={steps.MAIN}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              mainGoal={this.state.mainGoal}
              onChangeMainGoal={this.updateLocalState("mainGoal")}
            />
      </Route> */}
      <Route path={BASE_URL + steps.ACTIVITIES}>
        <GatherInfoGoalsActivities
              key={steps.ACTIVITIES}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              mainGoal={this.state.mainGoal}
              activities={this.state.activities}
              onChangeActivities={this.updateLocalState("activities")}
              otherActivity={this.state.otherActivity}
              onChangeOtherActivity={this.updateLocalState("otherActivity")}
              saveFormData={this.saveActivitiesToSession}
            />
      </Route>
      <Route path={BASE_URL + steps.PRIORITY}>
        <GatherInfoGoalsPriority
              key={steps.PRIORITY}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
              mainGoal={this.state.mainGoal}
              activities={this.state.activities}
              priority={this.state.priority}
              onChangePriority={this.updateLocalState("priority")}
              saveFormData={this.savePriorityToSession}
            />
      </Route>
      <Route path={BASE_URL + steps.PLATFORMS}>
        <GatherInfoGoalsPlatforms
              key={steps.PLATFORMS}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
              platforms={this.state.platforms}
              onChangePlatforms={this.updateLocalState("platforms")}
              otherPlatform={this.state.otherPlatform}
              onChangeOtherPlatform={this.updateLocalState("otherPlatform")}
              saveFormData={this.savePlatformsToSession}
            />
      </Route>
      <Route path={BASE_URL + steps.REVIEW}>
        <GatherInfoGoalsReview
              key={steps.REVIEW}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              mainGoal={this.state.mainGoal}
              activities={this.state.activities}
              priority={this.state.priority}
              platforms={this.state.platforms}
            />
      </Route>
      <Route path={BASE_URL}>
        <GatherInfoGoalsIntro
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
            />
      </Route>
    </Switch>
    </>
    );
  }
}

GatherInfoGoals.propTypes = propTypes;
GatherInfoGoals.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props
// "You can get access to the history object’s properties and the closest <Route>'s match 
// via the withRouter higher-order component. withRouter will pass updated 
// match, location, and history props to the wrapped component whenever it renders"
export default withRouter(GatherInfoGoals);
